// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Whether the player wants to be moved about (NFR-026, M3-11).
//
// Two independent things, deliberately kept apart:
//
//   * **`prefers-reduced-motion`** is the operating system's answer, given once by somebody who
//     may get motion sick or may simply find it unpleasant, and it is not a slider. When it is
//     set, the effect is *off* — not quieter.
//   * **An intensity setting** is a taste control for everybody else, 0 to 1.
//
// The system preference wins, and it wins by multiplication rather than by being consulted at
// each call site: an effect that has to remember to ask is an effect that will one day forget.
//
// Deliberately not in the core. This is a property of the person at the screen, and the same
// input trace re-simulated on a server has to produce the same score whether or not they get
// motion sick.

/** The media query the whole product asks. One string, so it cannot be typed two ways. */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

/**
 * The intensity positions the product steps through. Full, half, a hint, and off.
 *
 * **Here rather than in `MachineView`**, which is where it was until G2-01. Four things read it —
 * the view's own cycle, the settings page's four buttons, `preferences.ts`'s sync validator and now
 * the wrist menu's comfort row — and only the first of those wants a renderer. A wrist row reaching
 * for a Three.js module to find out what positions a slider has is a dependency nobody would defend
 * if it were being added rather than inherited, and `MotionPreference` is the object that already
 * owns this value at runtime.
 */
export const MOTION_STEPS: readonly number[] = [1, 0.5, 0.25, 0]

interface MediaQuerySource {
  matchMedia?: (query: string) => {
    matches: boolean
    addEventListener?: (type: 'change', listener: (e: { matches: boolean }) => void) => void
    removeEventListener?: (type: 'change', listener: (e: { matches: boolean }) => void) => void
  }
}

/**
 * Does the system ask for reduced motion?
 *
 * False where there is no `matchMedia` — a node test, a worker, an old WebView. Defaulting to
 * "no preference" rather than to "reduced" is the right way round: a headless environment has
 * nobody to make uncomfortable, and defaulting the other way would silently disable the effect
 * in every test that then proved it worked.
 */
export function prefersReducedMotion(source: MediaQuerySource = globalThis): boolean {
  const query = source.matchMedia?.(REDUCED_MOTION_QUERY)
  return query?.matches === true
}

/**
 * A live view of the preference, plus the player's own intensity.
 *
 * Live because the preference can change while the game is running — a player who starts to
 * feel unwell turns it on in system settings and expects the machine to settle immediately,
 * not at the next reload.
 */
export class MotionPreference {
  /** The player's taste control, 0 (off) to 1 (full). Clamped on write. */
  private taste = 1
  private reduced: boolean
  private detach: (() => void) | undefined

  constructor(source: MediaQuerySource = globalThis) {
    this.reduced = prefersReducedMotion(source)
    const query = source.matchMedia?.(REDUCED_MOTION_QUERY)
    if (query?.addEventListener === undefined) return

    const listener = (e: { matches: boolean }): void => {
      this.reduced = e.matches
    }
    query.addEventListener('change', listener)
    this.detach = () => query.removeEventListener?.('change', listener)
  }

  get reducedMotion(): boolean {
    return this.reduced
  }

  get intensity(): number {
    return this.taste
  }

  set intensity(value: number) {
    this.taste = Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0
  }

  /**
   * What every motion effect multiplies itself by.
   *
   * Exactly zero under the system preference, so an effect scaled by this does no work at all
   * rather than a hundredth of it — which matters, because "very small" motion is still motion
   * to somebody who asked for none.
   */
  get scale(): number {
    return this.reduced ? 0 : this.taste
  }

  dispose(): void {
    this.detach?.()
    this.detach = undefined
  }
}
